import { useState } from "react";
import { useApi, mutar } from "../../lib/useApi";
import { Card, Button, Input, Chip, SectionLabel, EmptyState, useToast, cn } from "../../components/ui";
import { PanelLateral, BloquePanel } from "../../components/ui/PanelLateral";
import type { SesionActiva } from "../../lib/tipos";

// Boticas (Ajustes › detalle). Cada fila dice si la botica está activa y cómo va HOY: abierta desde su primera
// venta o apertura del cajón, o cerrada con su cierre de caja (Kevin, 10-oct-2026: «en el momento que aperturan
// una caja, ya se ve que está activa»). UN botón «Editar» abre el panel con nombre, dirección y desactivar.
// El horario declarado salió de la pantalla por pedido de Kevin; la columna sigue en la base y, vacía, deja
// apagado el caso «venta fuera de horario».

type Sucursal = { id: string; nombre: string; direccion: string | null; activa: number; umbral_unidad?: number; umbral_envase?: number };
type EstadoHoy = { abierta_desde: string | null; cerrada_at: string | null };

const hora = (iso: string) => new Intl.DateTimeFormat("es-PE", { timeZone: "America/Lima", hour: "numeric", minute: "2-digit" }).format(new Date(iso));

function ChipHoy({ e }: { e: EstadoHoy | undefined }) {
  if (!e) return null;
  if (e.cerrada_at) return <Chip variant="neutral">Cerró hoy a las {hora(e.cerrada_at)}</Chip>;
  if (e.abierta_desde) return <Chip variant="ok">Abierta desde las {hora(e.abierta_desde)}</Chip>;
  return <Chip variant="neutral">Sin abrir hoy</Chip>;
}

export function Sucursales({ sesion }: { sesion: SesionActiva }) {
  const esSuper = sesion.usuario.rol === "super_admin";
  const lista = useApi<{ sucursales: Sucursal[] }>("/sucursales");
  const hoy = useApi<{ estado: Record<string, EstadoHoy> }>("/sucursales/hoy");
  const toast = useToast();
  const [nombre, setNombre] = useState("");
  const [direccion, setDireccion] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState<Sucursal | null>(null);

  async function crear() {
    if (!nombre.trim()) return;
    setEnviando(true);
    setError(null);
    try {
      await mutar("/sucursales", { method: "POST", body: { nombre: nombre.trim(), direccion: direccion.trim() } });
      setNombre("");
      setDireccion("");
      lista.recargar();
      hoy.recargar(); // la botica nueva necesita su estado de hoy (si no, sale sin etiqueta)
      toast("Botica creada.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviando(false);
    }
  }

  const items = lista.data?.sucursales ?? [];

  return (
    <div className="flex max-w-[820px] flex-col gap-4">
      <p className="text-[13px] leading-relaxed text-ink-2">
        Una botica aparece abierta desde su primera venta o apertura del cajón del día, y cerrada cuando hacen el cierre de caja.
        {!esSuper && " Si un dato de la botica cambió, avísale al Dueño: él lo actualiza."}
      </p>

      {esSuper && (
        <Card className="gap-3">
          <SectionLabel>Nueva botica</SectionLabel>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre" />
            <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Dirección (opcional)" />
          </div>
          {error && <p className="text-[12.5px] text-accent-ink">{error}</p>}
          <div>
            <Button onClick={() => void crear()} disabled={enviando || !nombre.trim()}>
              {enviando ? "Creando…" : "Crear botica"}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        {lista.cargando ? (
          <p className="py-4 text-center text-[13px] text-ink-3">Cargando boticas…</p>
        ) : lista.error ? (
          <div className="py-4 text-center">
            <p className="text-[13px] text-accent-ink">{lista.error}</p>
            <button onClick={lista.recargar} className="mt-2 text-[12.5px] text-link underline">Reintentar</button>
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Sin boticas" subtitle="Crea la primera para empezar." />
        ) : (
          <ul className="flex flex-col">
            {items.map((s) => (
              <li key={s.id} className="flex flex-col gap-2 border-b border-line-row py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className={cn("truncate text-[13px] font-semibold", s.activa !== 1 ? "text-ink-3" : "text-ink")}>{s.nombre}</p>
                    <Chip variant={s.activa === 1 ? "ok" : "neutral"}>{s.activa === 1 ? "Activa" : "Inactiva"}</Chip>
                    {s.activa === 1 && <ChipHoy e={hoy.data?.estado[s.id]} />}
                  </div>
                  <p className="truncate text-[12px] text-ink-2">{s.direccion ?? "Sin dirección"}</p>
                </div>
                {esSuper && (
                  <Button variant="outline" size="sm" onClick={() => setEditando(s)} className="min-h-[44px] shrink-0 self-start sm:min-h-[34px] sm:self-auto">
                    Editar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editando && (
        <EditarBotica
          key={editando.id}
          s={editando}
          onCerrar={() => setEditando(null)}
          onCambio={(msg) => {
            toast(msg);
            lista.recargar();
            hoy.recargar();
          }}
        />
      )}
    </div>
  );
}

function EditarBotica({ s, onCerrar, onCambio }: { s: Sucursal; onCerrar: () => void; onCambio: (mensaje: string) => void }) {
  const [ocupado, setOcupado] = useState(false);
  const [nombre, setNombre] = useState(s.nombre);
  const [direccion, setDireccion] = useState(s.direccion ?? "");
  // «Quedan pocas» en el buscador del Mostrador (Kevin, 16-oct-2026): una botica austera avisa con menos de
  // 10 tabletas; una grande, con menos de 50. Jarabes y demás envases: menos de 3.
  const [umbralUnidad, setUmbralUnidad] = useState(String(s.umbral_unidad ?? 10));
  const [umbralEnvase, setUmbralEnvase] = useState(String(s.umbral_envase ?? 3));
  const [error, setError] = useState<string | null>(null);
  const [confirmarBaja, setConfirmarBaja] = useState(false);

  async function guardar() {
    if (!nombre.trim()) {
      setError("Escribe el nombre de la botica.");
      return;
    }
    const unidad = Number(umbralUnidad);
    const envase = Number(umbralEnvase);
    if (!Number.isInteger(unidad) || unidad < 0 || !Number.isInteger(envase) || envase < 0) {
      setError("Los avisos de «quedan pocas» son números enteros (0 o más).");
      return;
    }
    setOcupado(true);
    setError(null);
    try {
      await mutar(`/sucursales/${s.id}`, {
        method: "PATCH",
        body: { nombre: nombre.trim(), direccion: direccion.trim(), umbral_unidad: unidad, umbral_envase: envase },
      });
      onCambio("Botica actualizada.");
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function cambiarEstado() {
    setOcupado(true);
    try {
      await mutar(`/sucursales/${s.id}`, { method: "PATCH", body: { activa: s.activa !== 1 } });
      onCambio(s.activa === 1 ? `${s.nombre} quedó inactiva.` : `${s.nombre} está activa.`);
      onCerrar();
    } finally {
      setOcupado(false);
    }
  }

  return (
    <PanelLateral titulo={`Editar ${s.nombre}`} subtitulo={s.direccion ?? undefined} onCerrar={onCerrar}>
      <BloquePanel titulo="Datos">
        <label className="flex flex-col gap-1 text-[12px] text-ink-2">
          Nombre
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} aria-label={`Nombre de ${s.nombre}`} />
        </label>
        <label className="flex flex-col gap-1 text-[12px] text-ink-2">
          Dirección
          <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} aria-label={`Dirección de ${s.nombre}`} placeholder="Opcional" />
        </label>
      </BloquePanel>
      <BloquePanel titulo="Aviso de «quedan pocas»">
        <p className="text-[12px] text-ink-2">
          En el buscador del Mostrador sale un triángulo amarillo cuando quedan menos que esto. Si a un producto le pusiste
          un mínimo en Inventario, manda ese mínimo.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-[12px] text-ink-2">
            Tabletas y cápsulas (unidades)
            <Input type="number" min={0} value={umbralUnidad} onChange={(e) => setUmbralUnidad(e.target.value)} aria-label={`Aviso por unidades de ${s.nombre}`} className="tabular-nums" />
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-ink-2">
            Jarabes y otros (envases)
            <Input type="number" min={0} value={umbralEnvase} onChange={(e) => setUmbralEnvase(e.target.value)} aria-label={`Aviso por envases de ${s.nombre}`} className="tabular-nums" />
          </label>
        </div>
      </BloquePanel>
      <div className="flex flex-col gap-1.5">
        <Button onClick={() => void guardar()} disabled={ocupado} className="min-h-[44px] justify-center">
          {ocupado ? "Guardando…" : "Guardar cambios"}
        </Button>
        {error && <p className="text-[12px] text-accent-ink">{error}</p>}
      </div>
      <BloquePanel titulo={s.activa === 1 ? "Desactivar" : "Activar"} peligro={s.activa === 1}>
        <p className="text-[12px] text-ink-2">
          {s.activa === 1 ? "La botica queda inactiva. Sus ventas, su stock y su historial se quedan." : "La botica vuelve a quedar activa."}
        </p>
        {s.activa === 1 && !confirmarBaja ? (
          <Button variant="outline" onClick={() => setConfirmarBaja(true)} disabled={ocupado} className="min-h-[44px] justify-center text-accent-ink">
            Desactivar botica
          </Button>
        ) : (
          <Button onClick={() => void cambiarEstado()} disabled={ocupado} className="min-h-[44px] justify-center">
            {s.activa === 1 ? "Sí, desactivar" : "Activar botica"}
          </Button>
        )}
      </BloquePanel>
    </PanelLateral>
  );
}
